import {
  type GlyphResult,
  getSealColorClass,
  getSealTextColor,
  getToneName,
  getSealFullName,
  getSealImageUrl,
} from "@/mocks/dreamspellData";

interface ResultDisplayProps {
  result: GlyphResult | null;
  dateStr: string;
  isLeap: boolean;
}

function GlyphCard({
  label,
  seal,
  tone,
  isMain = false,
  delay = 0,
}: {
  label: string;
  seal: GlyphResult["seal"];
  tone: GlyphResult["tone"];
  isMain?: boolean;
  delay?: number;
}) {
  const imageUrl = getSealImageUrl(seal.id);

  return (
    <div
      className={`rounded-xl border text-center overflow-hidden ${
        isMain
          ? "border-stone-400 bg-white"
          : "border-stone-200 bg-white/80"
      } animate-fade-in`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {imageUrl && (
        <div className={`flex justify-center pt-2 md:pt-4 ${isMain ? "px-2 md:px-6" : "px-1.5 md:px-5"}`}>
          <img
            src={imageUrl}
            alt={seal.name}
            className={`rounded-lg object-cover w-14 h-14 md:w-28 md:h-28`}
            loading="lazy"
          />
        </div>
      )}
      <div className={`${isMain ? "p-2 md:p-6 pt-1.5 md:pt-3" : "p-1.5 md:p-5 pt-1.5 md:pt-3"}`}>
        <div className="text-[9px] md:text-xs text-stone-500 uppercase tracking-wider mb-0.5 md:mb-2">
          {label}
        </div>
        <div className="flex items-center justify-center gap-1.5 md:gap-2 mb-0.5 md:mb-2">
          <span
            className={`inline-block w-2 h-2 md:w-2.5 md:h-2.5 rounded-full flex-shrink-0 ${getSealColorClass(seal.color)}`}
          />
          <span className={`font-semibold text-[11px] md:text-lg ${getSealTextColor(seal.color)}`}>
            {seal.name}
          </span>
        </div>
        <div className="text-[10px] md:text-base text-stone-600 mb-0 md:mb-1">
          {tone.name} · {tone.englishName}
        </div>
        <div className="text-[9px] md:text-xs text-stone-400">
          {seal.englishName}
        </div>
        <div className="mt-1 md:mt-2 text-[9px] md:text-sm text-stone-500 leading-relaxed">
          {seal.keywords}
        </div>
      </div>
    </div>
  );
}

export default function ResultDisplay({ result, dateStr, isLeap }: ResultDisplayProps) {
  if (isLeap) {
    return (
      <section className="w-full px-4 md:px-6 mt-8">
        <div className="max-w-5xl mx-auto">
          <div
            className="border border-amber-300 rounded-xl p-6 md:p-8 bg-amber-50/60 text-center animate-fade-in"
            key={`leap-${dateStr}`}
          >
            <div className="text-amber-700 font-semibold text-lg mb-2">
              {dateStr} · 校正日 / Hunab Ku (0.0)
            </div>
            <p className="text-amber-600 text-sm leading-relaxed">
              Dreamspell 將 2/29 視為「校正日 / Hunab Ku (0.0)」，不算入 Kin 編號。
              <br />
              這一天位於 2/28 與 3/1 之間，象徵宇宙的源頭與無限可能。
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!result) {
    return (
      <section className="w-full px-4 md:px-6 mt-8">
        <div className="max-w-5xl mx-auto">
          <div className="border border-dashed border-stone-300 rounded-xl p-6 md:p-10 text-center">
            <p className="text-stone-500 text-sm italic">
              輸入或選擇一個日期，星圖就會展開。
            </p>
          </div>
        </div>
      </section>
    );
  }

  const fullName = getSealFullName(result.seal, result.tone);

  return (
    <section className="w-full px-4 md:px-6 mt-8">
      <div className="max-w-5xl mx-auto" key={`result-${dateStr}`}>
        {/* Title area fades in first */}
        <div className="text-center mb-6 animate-fade-in">
          <div className="text-xs text-stone-500 uppercase tracking-widest mb-2">
            YOUR DREAMSPELL SIGNATURE
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800">
            {dateStr} · Kin {result.kin}
          </h2>
          <p className="mt-2 text-lg md:text-xl text-stone-600 font-medium">
            {fullName}
          </p>
        </div>

        {/* Cross layout: 3x3 grid with Main in center — staggered reveal */}
        <div className="grid grid-cols-3 gap-1.5 md:gap-4 max-w-4xl mx-auto">
          {/* Row 1: Top - Guide */}
          <div />
          <GlyphCard
            label="引導 · Guide"
            seal={result.guide.seal}
            tone={result.guide.tone}
            delay={180}
          />
          <div />

          {/* Row 2: Left - Challenge | Center - Main | Right - Support */}
          <GlyphCard
            label="挑戰 · Challenge"
            seal={result.challenge.seal}
            tone={result.challenge.tone}
            delay={360}
          />
          <GlyphCard
            label="主印記 · Main"
            seal={result.seal}
            tone={result.tone}
            isMain
            delay={0}
          />
          <GlyphCard
            label="支持 · Support"
            seal={result.support.seal}
            tone={result.support.tone}
            delay={540}
          />

          {/* Row 3: Bottom - Hidden */}
          <div />
          <GlyphCard
            label="隱藏 · Hidden / Occult"
            seal={result.hidden.seal}
            tone={result.hidden.tone}
            delay={720}
          />
          <div />
        </div>
      </div>
    </section>
  );
}